import React, { memo, useState, useEffect } from "react";
import api from "../../api/axios";
import styles from "./PlayerModal.module.css";
import { ConfirmationModal } from "../ConfirmationModal/ConfirmationModal.js";
import {
  LuUser,
  LuUsers,
  LuUpload,
  LuTrash2,
  LuPhone,
  LuTrophy,
  LuGraduationCap,
  LuMail,
  LuMapPin,
  LuSchool,
  LuBuilding,
  LuCalendar,
  LuHash,
  LuFilm,
  LuSquarePen
} from "react-icons/lu";
import { FaStar } from "react-icons/fa";
import { InputField, SelectField } from "../FormFields/FormFields.js";

const MAX_PROFILE_IMAGE_DIMENSION = 512;
const MAX_PROFILE_IMAGE_DATA_URL_LENGTH = 400000;
const PROFILE_IMAGE_OUTPUT_QUALITY = 0.72;
const PROFILE_IMAGE_OUTPUT_TYPE = "image/jpeg";

const loadImageFromFile = (file) =>
  new Promise((resolve, reject) => {
    const objectUrl = URL.createObjectURL(file);
    const image = new Image();

    image.onload = () => {
      URL.revokeObjectURL(objectUrl);
      resolve(image);
    };

    image.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error("Could not read that image file."));
    };

    image.src = objectUrl;
  });

const blobToDataUrl = (blob) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onloadend = () => resolve(reader.result);
    reader.onerror = () => reject(new Error("Could not convert the image for upload."));
    reader.readAsDataURL(blob);
  });

const compressProfileImage = async (file) => {
  if (!file.type.startsWith("image/")) {
    throw new Error("Only image uploads are supported.");
  }

  const image = await loadImageFromFile(file);
  const longestSide = Math.max(image.width, image.height, 1);
  const scale = Math.min(1, MAX_PROFILE_IMAGE_DIMENSION / longestSide);
  const width = Math.max(1, Math.round(image.width * scale));
  const height = Math.max(1, Math.round(image.height * scale));
  const canvas = document.createElement("canvas");
  const context = canvas.getContext("2d");

  if (!context) {
    throw new Error("Image compression is not available in this browser.");
  }

  canvas.width = width;
  canvas.height = height;
  context.drawImage(image, 0, 0, width, height);

  const blob = await new Promise((resolve, reject) => {
    canvas.toBlob(
      (generatedBlob) => {
        if (generatedBlob) {
          resolve(generatedBlob);
          return;
        }

        reject(new Error("Could not process the selected image."));
      },
      PROFILE_IMAGE_OUTPUT_TYPE,
      PROFILE_IMAGE_OUTPUT_QUALITY
    );
  });

  const dataUrl = await blobToDataUrl(blob);

  if (typeof dataUrl !== "string" || dataUrl.length > MAX_PROFILE_IMAGE_DATA_URL_LENGTH) {
    throw new Error("Image is still too large after compression. Please choose a smaller photo.");
  }

  return {
    dataUrl,
    width,
    height,
    sizeInKb: Math.max(1, Math.round(blob.size / 1024)),
  };
};

const getRenderableImageSrc = (value) => {
  if (!value || typeof value !== "string") {
    return "";
  }

  const trimmedValue = value.trim();

  if (trimmedValue.startsWith("data:image/") || trimmedValue.startsWith("blob:")) {
    return trimmedValue;
  }

  if (/^https?:\/\/.+\.(png|jpe?g|gif|webp|svg)(\?.*)?$/i.test(trimmedValue)) {
    return trimmedValue;
  }

  return "";
};

const PlayerModal = ({
  player,
  isAddingPlayer,
  onClose,
  onSave,
  onAdd,
  onDelete,
}) => {
  const [isEditMode, setIsEditMode] = useState(isAddingPlayer);

  // State for delete player confirmation modal
  const [isConfirmDelete, setIsConfirmDelete] = useState(false);

  // State for sports and positions
  const [sports, setSports] = useState([]);
  const [positions, setPositions] = useState([]);
  const [selectedSportId, setSelectedSportId] = useState(
    player?.sport_id || ""
  );
  const [rating, setRating] = useState(player?.stars || 0); // For interactive star updating
  const [missingFields, setMissingFields] = useState([]);
  const [imageUploadMessage, setImageUploadMessage] = useState("");
  const [imageUploadError, setImageUploadError] = useState("");

  // Fetch all sports when component mounts
  useEffect(() => {
    const fetchSports = async () => {
      try {
        const response = await api.get("/sports");
        setSports(response.data);
      } catch (error) {
        console.error("Error fetching sports:", error);
      }
    };
    fetchSports();
  }, []);

  // Fetch positions when selectedSportId changes
  useEffect(() => {
    const fetchPositions = async () => {
      if (!selectedSportId) {
        console.log("️No sport selected, clearing positions");
        setPositions([]);
        return;
      }

      try {
        const response = await api.get(`/sports/${selectedSportId}/positions`);

        if (response && response.data) {
          setPositions(response.data);
        }
      } catch (error) {
        console.error("Error fetching positions:", error);
        setPositions([]);
      }
    };

    fetchPositions();
  }, [selectedSportId]); // Re-fetch whenever sport changes

  // Fields to be rendered when editing player
  const defaultEditPlayer = {
    stars: "",
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    address: "",
    city: "",
    state: "",
    sport_id: "",
    position_id: "",
    secondary_position_id: "",
    jersey_number: "",
    highlight_video: "",
    grad_year: "",
    gpa: "",
    club_team: "",
    high_school: "",
    club_team_coach_name: "",
    club_team_coach_email: "",
    club_team_coach_phone: "",
    high_school_coach_name: "",
    high_school_coach_email: "",
    high_school_coach_phone: "",
    primary_parent_name: "",
    primary_parent_email: "",
    primary_parent_phone: "",
    secondary_parent_name: "",
    secondary_parent_email: "",
    secondary_parent_phone: "",
    profile_picture: "", // New field for profile picture (Base64 or URL)
  };

  // Fields to be rendered when adding player
  const defaultAddingPlayer = {
    sport_id: "",
    first_name: "",
    last_name: "",
    email: "",
    position_id: "",
    high_school: "",
    state: "",
  };

  // Required input fields when adding player
  const requiredFields = [
    "sport_id",
    "first_name",
    "last_name",
    "email",
    "position_id",
    "high_school",
    "state",
  ];

  const [editablePlayer, setEditablePlayer] = useState(
    isAddingPlayer
      ? defaultAddingPlayer
      : Object.fromEntries(
          Object.keys(defaultEditPlayer).map((key) => [
            key,
            player[key] ?? defaultEditPlayer[key],
          ])
        )
  );

  const handleChange = (e) => {
    const { name, value } = e.target;

    // If sport changes, update selectedSportId and reset position
    if (name === "sport_id") {
      setSelectedSportId(value);
      setEditablePlayer({
        ...editablePlayer,
        sport_id: value,
        position_id: "", // Reset position when sport changes
        secondary_position_id: "",
      });
    } else {
      setEditablePlayer({ ...editablePlayer, [name]: value });
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];

    if (!file) {
      return;
    }

    setImageUploadError("");

    try {
      const compressedImage = await compressProfileImage(file);

      setEditablePlayer((currentPlayer) => ({
        ...currentPlayer,
        profile_picture: compressedImage.dataUrl,
      }));
      setImageUploadMessage(
        `Compressed to ${compressedImage.width}x${compressedImage.height} at ${compressedImage.sizeInKb} KB before upload.`
      );
    } catch (error) {
      console.error("Error preparing profile picture:", error);
      setImageUploadMessage("");
      setImageUploadError(
        error instanceof Error ? error.message : "Could not process that image."
      );
    } finally {
      e.target.value = "";
    }
  };

  const handleRemoveImage = () => {
    setEditablePlayer((currentPlayer) => ({
      ...currentPlayer,
      profile_picture: "",
    }));
    setImageUploadMessage("");
    setImageUploadError("");
  };

  const handleSave = () => {
    // Form validation
    if (isAddingPlayer) {
      const errors = requiredFields.filter(
        (field) => !editablePlayer[field]?.trim()
      );

      if (errors.length > 0) {
        setMissingFields(errors);

        // Auto scroll to error
        const modal = document.querySelector(`.${styles.modalContent}`);
        if (modal) {
          const firstError = modal.querySelector(`[name="${errors[0]}"]`);
          firstError.scrollIntoView({
            block: "center",
            behavior: "smooth",
          });
        }
        return; // Cancel submission
      }

      setMissingFields([]); // Reset old errors
      onAdd(editablePlayer);
      onClose();
    } else {
      onSave({
        ...editablePlayer,
        player_id: player.player_id, // ensure player id is being sent over
      });
      setIsEditMode(false);
    }
  };

  // OnClick function for updating star rating interactively
  const handleStarClick = (index) => {
    if (!isEditMode) return;
    const newRating = index + 1;
    setRating(newRating);
    setEditablePlayer({ ...editablePlayer, stars: newRating });
  };

  const profileImageSrc = getRenderableImageSrc(editablePlayer.profile_picture);

  return (
  <>
    {/* Confirm Delete Modal */}
    {isConfirmDelete && (
      <ConfirmationModal
        message="Are you sure you want to delete this player"
        onConfirm={() => {
          onDelete(player.player_id);
          setIsConfirmDelete(false);
          onClose(); // close the main modal too
        }}
        onCancel={() => setIsConfirmDelete(false)}
      />
    )}
    <div className={styles.modalOverlay} onClick={onClose} />
    <div className={styles.modalContent}>
      <div className={styles.header}>
        <h2>{isAddingPlayer ? 'Add New Player' : 'Player Details'}</h2>
        <button className={styles.closeButton} onClick={onClose}>
          ×
        </button>
      </div>

      {/* Profile Section */}
      <div className={styles.profileSection}>
        <div className={styles.profileImageContainer}>
          {profileImageSrc ? (
            <img
              src={profileImageSrc}
              alt="Profile"
              className={styles.profilePicture}
            />
          ) : (
            <div className={styles.profilePicturePlaceholder}>
              <LuUser style={{ fontSize: "50px" }} />
            </div>
          )}

          {isEditMode && (
            <>
              <div className={styles.profileImageActions}>
                <button
                  type="button"
                  className={styles.uploadButton}
                  onClick={() => document.getElementById("player-modal-file-input").click()}
                  aria-label={profileImageSrc ? "Change profile picture" : "Upload profile picture"}
                  title={profileImageSrc ? "Change profile picture" : "Upload profile picture"}
                >
                  <LuUpload style={{ fontSize: "20px" }} />
                </button>

                {profileImageSrc && (
                  <button
                    type="button"
                    className={styles.removeImageButton}
                    onClick={handleRemoveImage}
                    aria-label="Remove profile picture"
                    title="Remove profile picture"
                  >
                    <LuTrash2 style={{ fontSize: "18px" }} />
                  </button>
                )}

                <input
                  id="player-modal-file-input"
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  className={styles.hiddenFileInput}
                />
              </div>

              {(imageUploadMessage || imageUploadError) && (
                <div
                  className={styles.imageUploadMessage}
                  style={{ color: imageUploadError ? "#c62828" : "#4b4b4b" }}
                >
                  {imageUploadError || imageUploadMessage}
                </div>
              )}
            </>
          )}
        </div>

        <div className={styles.profileHeader}>
          <h2 style={{margin:"0"}}> {player?.first_name || ' '} {player?.last_name || ' '} </h2>
          <p style={{margin:"0", color:"#4b4b4b"}}> {player?.position_name || ' '} </p>

          {[...Array(5)].map((_, i) => (
            <FaStar
              className={styles.starRating}
              key={i}
              onClick={() => handleStarClick(i)}
              style={{cursor: isEditMode ? "pointer" : "default"}}
              color={i < (isEditMode ? rating : editablePlayer.stars) ? "#ff3d00" : "#ddd"}
            />
          ))}
        </div>
        
        {!isEditMode ? (
          <button className={styles.editButton} style={{padding: "12px", marginLeft:"auto"}} onClick={() => setIsEditMode(true)}>
            <LuSquarePen style={{fontSize:"18px"}}/>
          </button>
        ) : ("")}
        

      </div>
      
      {/* Player Information Section */}
      <div className={styles.playerInformation}>

        {/* Personal Information */}
        <div className={styles.subHeader}>
          <LuUser className={styles.subHeaderIcon}/>
          <h3> Personal Information </h3>
        </div>
        <hr style={{ borderTop: "1px solid lightgrey", marginBottom: "20px" }} />
        <div className={styles.informationGrid}>
          <InputField
            label="First Name"
            name="first_name"
            value={editablePlayer.first_name}
            onChange={handleChange}
            isEditMode={isEditMode}
            Icon={LuUser}
            error={missingFields.includes("first_name")}
            required={isAddingPlayer && requiredFields.includes("first_name")}
            isAdding={isAddingPlayer}
          />
          <InputField
            label="Last Name"
            name="last_name"
            value={editablePlayer.last_name}
            onChange={handleChange}
            isEditMode={isEditMode}
            Icon={LuUser}
            error={missingFields.includes("last_name")}
            required={isAddingPlayer && requiredFields.includes("last_name")}
            isAdding={isAddingPlayer}
          />
          <InputField
            label="Phone"
            name="phone"
            value={editablePlayer.phone}
            onChange={handleChange}
            isEditMode={isEditMode}
            Icon={LuPhone}
            error={missingFields.includes("phone")}
            required={isAddingPlayer && requiredFields.includes("phone")}
            isAdding={isAddingPlayer}
          />
          <InputField
            label="Email"
            name="email"
            value={editablePlayer.email}
            onChange={handleChange}
            isEditMode={isEditMode}
            Icon={LuMail}
            error={missingFields.includes("email")}
            required={isAddingPlayer && requiredFields.includes("email")}
            isAdding={isAddingPlayer}
          />
          <InputField
            label="Address"
            name="address"
            value={editablePlayer.address}
            onChange={handleChange}
            isEditMode={isEditMode}
            Icon={LuMapPin}
            error={missingFields.includes("address")}
            required={isAddingPlayer && requiredFields.includes("address")}
            isAdding={isAddingPlayer}
          />
          <InputField
            label="City"
            name="city"
            value={editablePlayer.city}
            onChange={handleChange}
            isEditMode={isEditMode}
            Icon={LuMapPin}
            error={missingFields.includes("city")}
            required={isAddingPlayer && requiredFields.includes("city")}
            isAdding={isAddingPlayer}
          />
          <InputField
            label="State"
            name="state"
            value={editablePlayer.state}
            onChange={handleChange}
            isEditMode={isEditMode}
            Icon={LuMapPin}
            error={missingFields.includes("state")}
            required={isAddingPlayer && requiredFields.includes("state")}
            isAdding={isAddingPlayer}
          />
        </div>
        
        {/* Athletic Information */}
        <div className={styles.subHeader}>
          <LuTrophy className={styles.subHeaderIcon}/>
          <h3> Athletic Information </h3>
        </div>
        <hr style={{ borderTop: "1px solid lightgrey", marginBottom: "20px" }} />
        <div className={styles.informationGrid}>
          <SelectField
            label="Sport"
            name="sport_id"
            optionId="sport_id"
            optionName="sport_name"
            value={editablePlayer.sport_id}
            onChange={handleChange}
            options={sports}
            isEditMode={isEditMode}
            display={player?.sport_name}
            Icon={LuTrophy}
            error={missingFields.includes("sport_id")}
            required={isAddingPlayer && requiredFields.includes("sport_id")}
            isAdding={isAddingPlayer}
          />
          <InputField
            label="Jersey Number"
            name="jersey_number"
            value={editablePlayer.jersey_number}
            onChange={handleChange}
            isEditMode={isEditMode}
            Icon={LuHash}
            error={missingFields.includes("jersey_number")}
            required={isAddingPlayer && requiredFields.includes("jersey_number")}
            isAdding={isAddingPlayer}
          />
          <SelectField
            label="Position"
            name="position_id"
            optionId="position_id"
            optionName="position_name"
            value={editablePlayer.position_id}
            onChange={handleChange}
            options={positions}
            isEditMode={isEditMode}
            display={player?.position_name}
            Icon={LuTrophy}
            error={missingFields.includes("position_id")}
            required={isAddingPlayer && requiredFields.includes("position_id")}
            isAdding={isAddingPlayer}
          />
          <SelectField
            label="Secondary Position"
            name="secondary_position_id"
            optionId="position_id"
            optionName="position_name"
            value={editablePlayer.secondary_position_id}
            onChange={handleChange}
            options={positions}
            isEditMode={isEditMode}
            display={player?.secondary_position_name}
            Icon={LuTrophy}
            error={missingFields.includes("secondary_position_id")}
            required={isAddingPlayer && requiredFields.includes("secondary_position_id")}
            isAdding={isAddingPlayer}
          />
          <InputField
            label="Highlight Video"
            name="highlight_video"
            value={editablePlayer.highlight_video}
            onChange={handleChange}
            isEditMode={isEditMode}
            Icon={LuFilm}
            error={missingFields.includes("highlight_video")}
            required={isAddingPlayer && requiredFields.includes("highlight_video")}
            isAdding={isAddingPlayer}
          />
        </div>
        
        {/* Academic Information */}
        <div className={styles.subHeader}>
          <LuGraduationCap className={styles.subHeaderIcon}/>
          <h3> Academic Information </h3>
        </div>
        <hr style={{ borderTop: "1px solid lightgrey", marginBottom: "20px" }} />
        <div className={styles.informationGrid}>
          <InputField
            label="Graduation Year"
            name="grad_year"
            value={editablePlayer.grad_year}
            onChange={handleChange}
            isEditMode={isEditMode}
            Icon={LuCalendar}
            error={missingFields.includes("grad_year")}
            re  isAdding={isAddingPlayer}quired={isAddingPlayer && requiredFields.includes("grad_year")

            }

          />
          <InputField
            label="GPA"
            name="gpa"
            value={editablePlayer.gpa}
            onChange={handleChange}
            isEditMode={isEditMode}
            Icon={LuGraduationCap}
            error={missingFields.includes("gpa")}
            required={isAddingPlayer && requiredFields.includes("gpa")}
            isAdding={isAddingPlayer}
          />
          <InputField
            label="Club Team"
            name="club_team"
            value={editablePlayer.club_team}
            onChange={handleChange}
            isEditMode={isEditMode}
            Icon={LuBuilding}
            error={missingFields.includes("club_team")}
            required={isAddingPlayer && requiredFields.includes("club_team")}
            isAdding={isAddingPlayer}
          />
          <InputField
            label="High School"
            name="high_school"
            value={editablePlayer.high_school}
            onChange={handleChange}
            isEditMode={isEditMode}
            Icon={LuSchool}
            error={missingFields.includes("high_school")}
            required={isAddingPlayer && requiredFields.includes("high_school")}
            isAdding={isAddingPlayer}
          />
        </div>

        {/* Additional Contacts */}
        <div className={styles.subHeader}>
          <LuUsers className={styles.subHeaderIcon}/>
          <h3> Additional Contacts </h3>
        </div>
        <hr style={{ borderTop: "1px solid lightgrey", marginBottom: "20px" }} />
        <h4 className={styles.subLabels}>Club Coach</h4>
        <div className={styles.informationGrid}>
          <InputField
            label="Name"
            name="club_team_coach_name"
            value={editablePlayer.club_team_coach_name}
            onChange={handleChange}
            isEditMode={isEditMode}
            Icon={LuUsers}
            error={missingFields.includes("club_team_coach_name")}
            required={isAddingPlayer && requiredFields.includes("club_team_coach_name")}
            isAdding={isAddingPlayer}
          />
          <InputField
            label="Email"
            name="club_team_coach_email"
            value={editablePlayer.club_team_coach_email}
            onChange={handleChange}
            isEditMode={isEditMode}
            Icon={LuMail}
            error={missingFields.includes("club_team_coach_email")}
            required={isAddingPlayer && requiredFields.includes("club_team_coach_email")}
            isAdding={isAddingPlayer}
          />
        </div>
        <h4 className={styles.subLabels}>High School Coach</h4>
        <div className={styles.informationGrid}>
          <InputField
            label="Name"
            name="high_school_coach_name"
            value={editablePlayer.high_school_coach_name}
            onChange={handleChange}
            isEditMode={isEditMode}
            Icon={LuUsers}
            error={missingFields.includes("high_school_coach_name")}
            required={isAddingPlayer && requiredFields.includes("high_school_coach_name")}
            isAdding={isAddingPlayer}
          />
          <InputField
            label="Email"
            name="high_school_coach_email"
            value={editablePlayer.high_school_coach_email}
            onChange={handleChange}
            isEditMode={isEditMode}
            Icon={LuMail}
            error={missingFields.includes("high_school_coach_email")}
            required={isAddingPlayer && requiredFields.includes("high_school_coach_email")}
            isAdding={isAddingPlayer}
          />
        </div>
        <h4 className={styles.subLabels}>Primary Parent</h4>
        <div className={styles.informationGrid}>
          <InputField
            label="Name"
            name="primary_parent_name"
            value={editablePlayer.primary_parent_name}
            onChange={handleChange}
            isEditMode={isEditMode}
            Icon={LuUser}
            error={missingFields.includes("primary_parent_name")}
            required={isAddingPlayer && requiredFields.includes("primary_parent_name")}
            isAdding={isAddingPlayer}
          />
          <InputField
            label="Phone"
            name="primary_parent_phone"
            value={editablePlayer.primary_parent_phone}
            onChange={handleChange}
            isEditMode={isEditMode}
            Icon={LuPhone}
            error={missingFields.includes("primary_parent_phone")}
            required={isAddingPlayer && requiredFields.includes("primary_parent_phone")}
            isAdding={isAddingPlayer}
          />
          <InputField
            label="Email"
            name="primary_parent_email"
            value={editablePlayer.primary_parent_email}
            onChange={handleChange}
            isEditMode={isEditMode}
            Icon={LuMail}
            error={missingFields.includes("primary_parent_email")}
            required={isAddingPlayer && requiredFields.includes("primary_parent_email")}
            isAdding={isAddingPlayer}
          />
        </div>
        <h4 className={styles.subLabels}>Secondary Parent</h4>
        <div className={styles.informationGrid}>
          <InputField
            label="Name"
            name="secondary_parent_name"
            value={editablePlayer.secondary_parent_name}
            onChange={handleChange}
            isEditMode={isEditMode}
            Icon={LuUser}
            error={missingFields.includes("secondary_parent_name")}
            required={isAddingPlayer && requiredFields.includes("secondary_parent_name")}
            isAdding={isAddingPlayer}
          />
          <InputField
            label="Phone"
            name="secondary_parent_phone"
            value={editablePlayer.secondary_parent_phone}
            onChange={handleChange}
            isEditMode={isEditMode}
            Icon={LuPhone}
            error={missingFields.includes("secondary_parent_phone")}
            required={isAddingPlayer && requiredFields.includes("secondary_parent_phone")}
            isAdding={isAddingPlayer}
          />
          <InputField
            label="Email"
            name="secondary_parent_email"
            value={editablePlayer.secondary_parent_email}
            onChange={handleChange}
            isEditMode={isEditMode}
            Icon={LuMail}
            error={missingFields.includes("secondary_parent_email")}
            required={isAddingPlayer && requiredFields.includes("secondary_parent_email")}
            isAdding={isAddingPlayer}
          />
        </div>
      </div>

      {/* Button Group */}
      <div className={styles.buttonGroup}>
        {isEditMode ? (
          <>
          <button className={styles.editButton} onClick={handleSave}>
            {isAddingPlayer ? 'Add Player' : 'Save'}
          </button>
            {!isAddingPlayer && (
              <button
                onClick={() => {
                  setEditablePlayer({ ...player });
                  setIsEditMode(false);
                  setSelectedSportId(player.sport_id);
                  setRating(player.stars || 0);
                }}
                className={styles.cancelButton}
              >
                Cancel
              </button>
            )}
          </>
        ) : (
          <>
            <button className={styles.editButton} onClick={() => setIsEditMode(true)}>
              Edit
            </button>
            <button
              onClick={() => setIsConfirmDelete(true)}
              className={styles.deleteButton}
            >
              Delete Player
            </button>
          </>
        )}
      </div>
    </div>
  </>
  );
};

export default PlayerModal;
